import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { toast } from 'react-hot-toast';
import ConfirmModal from './ConfirmModal';
import { CAP } from '../utils/permissions';
import { usePermissions, ReadOnlyNotice, inertClasses } from '../utils/PermissionContext';
import { errorText } from '../utils/apiErrors';

// Helper function to safely render Firestore values that might be raw objects containing {stringValue: ...}
const renderValue = (val) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'object') {
        if ('stringValue' in val) return val.stringValue;
        if ('integerValue' in val) return val.integerValue;
        return JSON.stringify(val);
    }
    return String(val);
};

const AdminCustomers = ({ googleIdToken, t }) => {
    const [customers, setCustomers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all'); // all, prepaid, postpaid

    const { apiRequest } = useApi(googleIdToken, () => window.location.reload());
    // Read-only accounts can see this whole screen but must not act on it, so
    // every control that writes is hidden or inert below.
    const { can } = usePermissions();
    const canWrite = can(CAP.ADMIN_WRITE);
    const navigate = useNavigate();
    const [updatingCustomerId, setUpdatingCustomerId] = useState(null);
    const [updatingField, setUpdatingField] = useState(null); // 'postpaid' | 'active'
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncConfirmOpen, setSyncConfirmOpen] = useState(false);

    const handlePostpaidToggle = async (e, customer) => {
        e.stopPropagation(); // Prevent card navigation click!
        if (updatingCustomerId) return;

        const custId = renderValue(customer.customerId) || renderValue(customer.id);
        if (!custId) return;

        setUpdatingCustomerId(customer.id);
        setUpdatingField('postpaid');
        const currentPostpaid = !!customer.isPostpaid;
        const newPostpaid = !currentPostpaid;

        try {
            const updatedCustomer = {
                ...customer,
                isPostpaid: newPostpaid
            };

            const data = await apiRequest(`/api/customers/${custId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(updatedCustomer)
            });

            if (data && !data.error) {
                setCustomers(prev => prev.map(c =>
                    c.id === customer.id ? { ...c, isPostpaid: newPostpaid } : c
                ));
                toast.success(`${renderValue(customer.customerName || customer.name)} ${t?.updated_to || 'updated to'} ${newPostpaid ? (t?.postpaid || 'Postpaid') : (t?.prepaid || 'Prepaid')}!`);
                // Clear sessionStorage caches to ensure data consistency
                try {
                    sessionStorage.removeItem('admin_customers_cache');
                    Object.keys(sessionStorage).forEach(key => {
                        if (key.startsWith('dashboard_initial_data_')) {
                            sessionStorage.removeItem(key);
                        }
                    });
                } catch (e) {
                    console.error("Failed to clear sessionStorage after postpaid toggle:", e);
                }
            } else {
                toast.error(errorText(data, t, t?.failed_update_postpaid || "Failed to update postpaid status."));
            }
        } catch (error) {
            console.error("Error updating postpaid status:", error);
            toast.error(t?.failed_update_postpaid || "Failed to update postpaid status.");
        } finally {
            setUpdatingCustomerId(null);
            setUpdatingField(null);
        }
    };

    const handleToggleActive = async (e, customer) => {
        e.stopPropagation(); // Prevent card navigation click!
        if (updatingCustomerId) return;

        const custId = renderValue(customer.customerId) || renderValue(customer.id);
        if (!custId) return;

        setUpdatingCustomerId(customer.id);
        setUpdatingField('active');
        const currentActive = customer.isActive !== false;
        const newActive = !currentActive;

        try {
            // Minimal payload: partial update avoids persisting computed fields.
            const data = await apiRequest(`/api/customers/${custId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ isActive: newActive })
            });

            if (data && !data.error) {
                setCustomers(prev => prev.map(c =>
                    c.id === customer.id ? { ...c, isActive: newActive } : c
                ));
                toast.success(`${renderValue(customer.customerName || customer.name)} ${t?.set_to || 'set to'} ${newActive ? (t?.active || 'Active') : (t?.inactive || 'Inactive')}!`);
                // Clear sessionStorage caches to ensure data consistency
                try {
                    sessionStorage.removeItem('admin_customers_cache');
                    Object.keys(sessionStorage).forEach(key => {
                        if (key.startsWith('dashboard_initial_data_')) {
                            sessionStorage.removeItem(key);
                        }
                    });
                } catch (e) {
                    console.error("Failed to clear sessionStorage after active toggle:", e);
                }
            } else {
                toast.error(errorText(data, t, t?.failed_update_active || "Failed to update active status."));
            }
        } catch (error) {
            console.error("Error updating active status:", error);
            toast.error(t?.failed_update_active || "Failed to update active status.");
        } finally {
            setUpdatingCustomerId(null);
            setUpdatingField(null);
        }
    };

    const handleToggleAutoInvoice = async (e, customer) => {
        e.stopPropagation(); // Prevent card navigation click!
        if (updatingCustomerId) return;

        const custId = renderValue(customer.customerId) || renderValue(customer.id);
        if (!custId) return;

        setUpdatingCustomerId(customer.id);
        setUpdatingField('auto_invoice');
        const currentAutoInvoice = customer.isAutoInvoiceEnabled !== false;
        const newAutoInvoice = !currentAutoInvoice;

        try {
            // Minimal payload: partial update avoids persisting computed fields.
            const data = await apiRequest(`/api/customers/${custId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ isAutoInvoiceEnabled: newAutoInvoice })
            });

            if (data && !data.error) {
                setCustomers(prev => prev.map(c =>
                    c.id === customer.id ? { ...c, isAutoInvoiceEnabled: newAutoInvoice } : c
                ));
                toast.success(`${renderValue(customer.customerName || customer.name)} ${t?.set_to || 'set to'} ${newAutoInvoice ? (t?.auto_invoice_on || 'Auto Invoice ON') : (t?.auto_invoice_off || 'Auto Invoice OFF')}!`);
                // Clear sessionStorage caches to ensure data consistency
                try {
                    sessionStorage.removeItem('admin_customers_cache');
                    Object.keys(sessionStorage).forEach(key => {
                        if (key.startsWith('dashboard_initial_data_')) {
                            sessionStorage.removeItem(key);
                        }
                    });
                } catch (e) {
                    console.error("Failed to clear sessionStorage after auto invoice toggle:", e);
                }
            } else {
                toast.error(errorText(data, t, t?.failed_update_auto_invoice || "Failed to update Auto Invoice status."));
            }
        } catch (error) {
            console.error("Error updating auto invoice status:", error);
            toast.error(t?.failed_update_auto_invoice || "Failed to update Auto Invoice status.");
        } finally {
            setUpdatingCustomerId(null);
            setUpdatingField(null);
        }
    };

    const handleManualSync = async () => {
        if (isSyncing) return;
        setIsSyncing(true);
        const loadingToast = toast.loading(t?.starting_sync || 'Starting customer sync...');
        try {
            const data = await apiRequest('/api/sync_customers/trigger', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            if (data && data.success) {
                toast.success(
                    data.message || (t?.sync_started || "Customer sync started in the background. Track progress in 'Sync Customers Logs'."),
                    { id: loadingToast, duration: 6000 }
                );
                setSyncConfirmOpen(false);
            } else {
                toast.error(errorText(data, t, t?.sync_failed || 'Failed to start customer sync.'), { id: loadingToast });
            }
        } catch (error) {
            console.error('Error triggering customer sync:', error);
            toast.error(errorText(error, t, t?.sync_failed || 'Failed to start customer sync.'), { id: loadingToast });
        } finally {
            setIsSyncing(false);
        }
    };

    useEffect(() => {
        const cacheKey = `admin_customers_cache`;

        // Stale-while-revalidate: paint cached customers instantly (no skeleton),
        // then ALWAYS re-fetch in the background and reconcile. This keeps the
        // screen snappy while ensuring newly-synced customers appear on the next
        // visit/reload — previously the cache short-circuited the API entirely, so
        // new customers stayed hidden until "Clear site data" wiped sessionStorage.
        const fetchCustomers = async () => {
            let paintedFromCache = false;

            const cachedData = sessionStorage.getItem(cacheKey);
            if (cachedData) {
                try {
                    const parsed = JSON.parse(cachedData);
                    // Ignore caches created before `isActive` existed (would render everyone active).
                    const isStale = Array.isArray(parsed) && parsed.length > 0 && !('isActive' in parsed[0]);
                    if (Array.isArray(parsed) && !isStale) {
                        setCustomers(parsed);
                        setIsLoading(false);
                        paintedFromCache = true;
                    } else if (isStale) {
                        sessionStorage.removeItem(cacheKey);
                    }
                } catch (e) {
                    console.error("Error parsing cached admin customers:", e);
                    sessionStorage.removeItem(cacheKey);
                }
            }

            // Only show the skeleton when there's nothing cached to paint yet.
            if (!paintedFromCache) {
                setIsLoading(true);
            }

            // Always revalidate against the API and refresh state + cache.
            try {
                // Fetch all customers (for Admin, /api/customers fetches all)
                const data = await apiRequest('/api/customers');
                if (Array.isArray(data)) {
                    setCustomers(data);
                    // Refresh the sessionStorage cache with the latest data.
                    sessionStorage.setItem(cacheKey, JSON.stringify(data));
                } else if (data && data.error && !paintedFromCache) {
                    toast.error(errorText(data, t));
                }
            } catch (error) {
                console.error("Error fetching customers:", error);
                // Keep the cached view on a background-refresh failure; only alert
                // the user when there was nothing to show in the first place.
                if (!paintedFromCache) {
                    toast.error(t?.failed_load_customers || "Failed to load customers.");
                }
            } finally {
                setIsLoading(false);
            }
        };

        if (googleIdToken) {
            fetchCustomers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [googleIdToken]);

    // Search and Filter Logic
    const filteredCustomers = useMemo(() => {
        const lowerSearch = searchTerm.toLowerCase();
        return customers.filter(c => {
            const nameStr = renderValue(c.customerName || c.name);
            const domainStr = renderValue(c.customerDomain);
            const idStr = renderValue(c.customerId);
            const rawIdStr = renderValue(c.id);

            const debtorStr = renderValue(c.afasDebtorNum).trim();
            const billingDebtorStr = renderValue(c.billingDebtorNr).trim();

            const matchesSearch =
                nameStr.toLowerCase().includes(lowerSearch) ||
                domainStr.toLowerCase().includes(lowerSearch) ||
                idStr.toLowerCase().includes(lowerSearch) ||
                rawIdStr.toLowerCase().includes(lowerSearch) ||
                debtorStr.toLowerCase().includes(lowerSearch) ||
                billingDebtorStr.toLowerCase().includes(lowerSearch);

            if (filterType === 'all') return matchesSearch;
            if (filterType === 'postpaid') return matchesSearch && c.isPostpaid === true;
            if (filterType === 'prepaid') return matchesSearch && c.isPostpaid !== true;
            if (filterType === 'active') return matchesSearch && c.isActive !== false;
            if (filterType === 'inactive') return matchesSearch && c.isActive === false;
            if (filterType === 'auto_invoice_off') return matchesSearch && c.isAutoInvoiceEnabled === false;

            if (filterType === 'mismatch') {
                // Mismatch means both are configured but they are different
                const isMismatch = !!debtorStr && !!billingDebtorStr && debtorStr !== billingDebtorStr;
                return matchesSearch && isMismatch;
            }

            if (filterType === 'unset') {
                // Unset means there is no matching AFAS billing record in the database
                const isUnset = !billingDebtorStr;
                return matchesSearch && isUnset;
            }

            return matchesSearch;
        });
    }, [customers, searchTerm, filterType]);



    const filterOptions = [
        { label: t?.all_customers || 'All Customers', value: 'all', activeClass: 'bg-primary text-white border-primary shadow-sm shadow-primary/20', inactiveClass: 'bg-white hover:bg-gray-50 text-gray-600 border-gray-200' },
        { label: t?.postpaid || 'Postpaid', value: 'postpaid', activeClass: 'bg-amber-500 text-white border-amber-500 shadow-sm shadow-amber-500/20', inactiveClass: 'bg-white hover:bg-amber-50/50 text-amber-700 border-amber-200' },
        { label: t?.active || 'Active', value: 'active', activeClass: 'bg-green-600 text-white border-green-600 shadow-sm shadow-green-600/20', inactiveClass: 'bg-white hover:bg-green-50/50 text-green-700 border-green-200' },
        { label: t?.inactive || 'Inactive', value: 'inactive', activeClass: 'bg-gray-600 text-white border-gray-600 shadow-sm shadow-gray-600/20', inactiveClass: 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300' },
        { label: t?.mismatch || 'Mismatch', value: 'mismatch', activeClass: 'bg-rose-600 text-white border-rose-600 shadow-sm shadow-rose-600/20', inactiveClass: 'bg-white hover:bg-rose-50/50 text-rose-700 border-rose-200' },
        { label: t?.unset_billing_debtor || 'Unset Billing Debtor', value: 'unset', activeClass: 'bg-purple-600 text-white border-purple-600 shadow-sm shadow-purple-600/20', inactiveClass: 'bg-white hover:bg-purple-50/50 text-purple-700 border-purple-200' },
        { label: t?.auto_invoice_off || 'Auto Factuur UIT', value: 'auto_invoice_off', activeClass: 'bg-indigo-600 text-white border-indigo-600 shadow-sm shadow-indigo-600/20', inactiveClass: 'bg-white hover:bg-indigo-50/50 text-indigo-700 border-indigo-200' }
    ];

    return (
        <div className="p-4 sm:p-8 space-y-8">
            {/* Page Header: title on the left, primary action on the right */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800 flex items-center shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-3 bg-primary text-white rounded-full p-1.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        {t?.admin_customers || 'Customers'}
                    </h1>
                    <p className="text-gray-600 mt-2">{t?.admin_customers_description || 'Overview and detailed verification of all customers.'}</p>
                </div>
                {canWrite && <button
                    onClick={() => setSyncConfirmOpen(true)}
                    disabled={isSyncing}
                    title={t?.sync_now_tooltip || 'Run the full customer sync (Google Workspace + JIRA + AFAS) now'}
                    className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white shadow-sm transition-colors shrink-0 self-start sm:self-auto ${isSyncing ? 'bg-primary/60 cursor-not-allowed' : 'bg-primary hover:bg-opacity-90'}`}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {isSyncing ? (t?.syncing || 'Syncing…') : (t?.sync_now || 'Sync now')}
                </button>}
            </div>

            <ReadOnlyNotice t={t} />

                {/* Filters and Search Bar */}
                <div className="flex flex-col xl:flex-row gap-4 w-full xl:w-auto xl:items-center">
                    {/* Quick Selection Tray */}
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-bold text-gray-400 uppercase tracking-wider select-none shrink-0 mr-1">
                            {t?.quick_selection || 'Quick selection:'}
                        </span>
                        {filterOptions.map((opt) => {
                            const isActive = filterType === opt.value;
                            return (
                                <button
                                    key={opt.value}
                                    onClick={() => setFilterType(opt.value)}
                                    className={`px-3.5 py-1.5 rounded-full text-xs font-bold border transition duration-200 shrink-0 select-none ${isActive ? opt.activeClass : opt.inactiveClass
                                        }`}
                                >
                                    {opt.label}
                                </button>
                            );
                        })}
                    </div>

                    {/* Full Filter Dropdown */}
                    <select
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                        className="p-2 border border-gray-300 rounded-lg bg-white text-sm text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-semibold cursor-pointer select-none"
                    >
                        <option value="all">{t?.all_customers || 'All Customers'}</option>
                        <option value="postpaid">{t?.postpaid_only || 'Postpaid Only'}</option>
                        <option value="prepaid">{t?.prepaid_only || 'Prepaid Only'}</option>
                        <option value="active">{t?.active_only || 'Active Only'}</option>
                        <option value="inactive">{t?.inactive_only || 'Inactive Only'}</option>
                        <option value="mismatch">{t?.mismatch_only || 'Mismatch Only'}</option>
                        <option value="unset">{t?.unset_billing_debtor || 'Unset Billing Debtor'}</option>
                        <option value="auto_invoice_off">{t?.auto_invoice_off || 'Auto Factuur UIT'}</option>
                    </select>

                    {/* Search Bar */}
                    <div className="relative w-full xl:w-64">
                        <input
                            type="text"
                            placeholder={t?.search_customers || 'Search customers...'}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                        />
                        <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                        </svg>
                    </div>
                </div>

            {/* Customers Card Grid */}
            {isLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3, 4, 5, 6].map((idx) => (
                        <div key={idx} className="bg-white rounded-xl border border-primary/5 overflow-hidden shadow-sm flex flex-col h-full animate-pulse select-none pointer-events-none">
                            {/* Shimmer Top Banner */}
                            <div className="h-2 bg-gradient-to-r from-primary/20 via-primary/35 to-primary/20"></div>

                            <div className="p-6 flex flex-col flex-grow justify-between space-y-4">
                                <div className="flex justify-between items-start gap-3">
                                    <div className="space-y-2 min-w-0 flex-grow">
                                        {/* Name Skeleton */}
                                        <div className="h-5 bg-primary/20 rounded-md w-3/4"></div>
                                        {/* Domain Skeleton */}
                                        <div className="h-4 bg-primary/10 rounded-md w-1/2"></div>
                                    </div>
                                    {/* Right Pill Skeletons */}
                                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                                        <div className="h-5 bg-primary/15 rounded-full w-14"></div>
                                        <div className="h-4.5 bg-primary/10 rounded-full w-16"></div>
                                    </div>
                                </div>

                                {/* Comparative Debtor Box Skeleton */}
                                <div className="rounded-xl p-3 bg-primary/5 border border-primary/10 flex flex-col space-y-2.5">
                                    <div className="flex justify-between items-center">
                                        <div className="space-y-1.5">
                                            <div className="h-3 bg-primary/10 rounded w-20"></div>
                                            <div className="h-4.5 bg-primary/20 rounded w-16"></div>
                                        </div>
                                        <div className="space-y-1.5 text-right">
                                            <div className="h-3 bg-primary/10 rounded w-16"></div>
                                            <div className="h-4.5 bg-primary/20 rounded w-12"></div>
                                        </div>
                                    </div>

                                    <div className="border-t border-primary/10 border-dashed pt-2.5 flex justify-between items-center">
                                        <div className="space-y-1.5">
                                            <div className="h-3 bg-primary/10 rounded w-24"></div>
                                            <div className="h-4 bg-primary/20 rounded w-16"></div>
                                        </div>
                                    </div>
                                </div>

                                {/* Bottom Row Skeleton */}
                                <div className="pt-2 border-t border-primary/5 flex justify-between items-center">
                                    <div className="h-3 bg-primary/10 rounded w-16"></div>
                                    <div className="h-3.5 w-3.5 bg-primary/15 rounded-full"></div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : filteredCustomers.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredCustomers.map((customer) => {
                        const nameStr = renderValue(customer.customerName);
                        const domainStr = renderValue(customer.customerDomain);
                        const idStr = renderValue(customer.customerId);

                        const debtorStr = renderValue(customer.afasDebtorNum).trim();
                        const billingDebtorStr = renderValue(customer.billingDebtorNr).trim();
                        const hasDebtorId = !!debtorStr;

                        let isMismatch = false;
                        if (debtorStr && billingDebtorStr) {
                            isMismatch = debtorStr !== billingDebtorStr;
                        } else if (debtorStr || billingDebtorStr) {
                            // If one is present and the other is empty, we consider it a mismatch
                            isMismatch = true;
                        }

                        // Calculate total purchased licenses
                        const subscriptions = customer.subscriptions || [];
                        let totalPurchasedLicenses = 0;
                        subscriptions.forEach(sub => {
                            const val = renderValue(sub.purchasedLicenses);
                            const num = parseInt(val, 10);
                            if (!isNaN(num)) {
                                totalPurchasedLicenses += num;
                            }
                        });

                        return (
                            <div
                                key={customer.id}
                                onClick={() => navigate(`/admin/customers/${customer.id}`)}
                                className={`bg-white rounded-xl shadow-sm hover:shadow-md border cursor-pointer overflow-hidden transform hover:-translate-y-1 transition duration-300 flex flex-col h-full ${isMismatch ? 'border-rose-200 hover:border-rose-300' : 'border-gray-100 hover:border-gray-200'
                                    }`}
                            >
                                {/* Card Banner Decoration */}
                                <div className={`h-2 bg-gradient-to-r ${isMismatch
                                    ? 'from-rose-500 via-red-500 to-orange-500'
                                    : 'from-primary via-indigo-500 to-purple-600'
                                    }`}></div>

                                <div className="p-6 flex flex-col flex-grow justify-between space-y-4">
                                    <div className="flex justify-between items-start gap-3">
                                        {/* Left Side: Name & Domain */}
                                        <div className="space-y-1.5 min-w-0 flex-grow">
                                            <h2 className="text-xl font-bold text-gray-900 truncate" title={nameStr || domainStr}>
                                                {nameStr || domainStr || (t?.no_name || 'No Name')}
                                            </h2>
                                            <p className="text-sm font-medium text-gray-500 flex items-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.6 9h16.8M3.6 15h16.8" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3a15.3 15.3 0 014.5 9 15.3 15.3 0 01-4.5 9 15.3 15.3 0 01-4.5-9 15.3 15.3 0 014.5-9z" />
                                                </svg>
                                                <span className="truncate">{domainStr || 'N/A'}</span>
                                            </p>
                                        </div>

                                        {/* Right Side: Badges.
                                            These two are status *and* toggle, so for a
                                            read-only account they stay put as inert pills
                                            rather than disappearing — hiding them would
                                            hide the postpaid/active state itself. Pure
                                            actions (Sync now) are removed entirely. */}
                                        <div className="flex flex-col items-end gap-1.5 shrink-0">
                                            <button
                                                onClick={(e) => handlePostpaidToggle(e, customer)}
                                                disabled={!canWrite || updatingCustomerId !== null}
                                                title={canWrite
                                                    ? (t?.toggle_postpaid_status || 'Click to toggle Postpaid / Prepaid status')
                                                    : undefined}
                                                className={`text-[10px] font-bold uppercase px-2.5 py-0.5 rounded-full border transition-all duration-200 select-none flex items-center gap-1 focus:outline-none ${!canWrite ? 'cursor-default' : updatingCustomerId !== null ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:scale-105 active:scale-95'
                                                    } ${inertClasses(customer.isPostpaid
                                                        ? 'bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200 hover:border-amber-300 shadow-sm shadow-amber-500/5'
                                                        : 'bg-teal-100 text-teal-800 border-teal-200 hover:bg-teal-200 hover:border-teal-300 shadow-sm shadow-teal-500/5',
                                                        !canWrite)
                                                    }`}
                                            >
                                                <span className={`h-1 w-1 rounded-full ${customer.isPostpaid ? 'bg-amber-600' : 'bg-teal-600'}`}></span>
                                                {customer.isPostpaid ? (t?.postpaid || 'Postpaid') : (t?.prepaid || 'Prepaid')}
                                                {updatingCustomerId === customer.id && updatingField === 'postpaid' && (
                                                    <svg className="animate-spin h-2.5 w-2.5 text-current ml-0.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                )}
                                            </button>
                                            <button
                                                onClick={(e) => handleToggleActive(e, customer)}
                                                disabled={!canWrite || updatingCustomerId !== null}
                                                title={canWrite
                                                    ? (t?.toggle_active_status || 'Click to toggle Active / Inactive status')
                                                    : undefined}
                                                className={`text-[10px] font-bold uppercase px-2.5 py-0.5 rounded-full border transition-all duration-200 select-none flex items-center gap-1 focus:outline-none ${!canWrite ? 'cursor-default' : updatingCustomerId !== null ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:scale-105 active:scale-95'
                                                    } ${inertClasses(customer.isActive !== false
                                                        ? 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200 hover:border-green-300 shadow-sm shadow-green-500/5'
                                                        : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200 hover:border-gray-300',
                                                        !canWrite)
                                                    }`}
                                            >
                                                <span className={`h-1 w-1 rounded-full ${customer.isActive !== false ? 'bg-green-600' : 'bg-gray-500'}`}></span>
                                                {customer.isActive !== false ? (t?.active || 'Active') : (t?.inactive || 'Inactive')}
                                                {updatingCustomerId === customer.id && updatingField === 'active' && (
                                                    <svg className="animate-spin h-2.5 w-2.5 text-current ml-0.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                )}
                                            </button>
                                                <button
                                                    onClick={(e) => handleToggleAutoInvoice(e, customer)}
                                                    disabled={!canWrite || updatingCustomerId !== null}
                                                    title={canWrite
                                                        ? (t?.toggle_auto_invoice || 'Click to toggle Auto Invoice')
                                                        : undefined}
                                                    className={`text-[10px] font-bold uppercase px-2.5 py-0.5 rounded-full border transition-all duration-200 select-none flex items-center gap-1 focus:outline-none ${!canWrite ? 'cursor-default' : updatingCustomerId !== null ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:scale-105 active:scale-95'
                                                        } ${inertClasses(customer.isAutoInvoiceEnabled !== false ? 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200 hover:border-blue-300 shadow-sm shadow-blue-500/5' : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200 hover:border-gray-400 shadow-sm shadow-gray-500/5',
                                                            !canWrite)
                                                        }`}
                                                >
                                                    <span className={`h-1 w-1 rounded-full ${customer.isAutoInvoiceEnabled !== false ? 'bg-blue-600' : 'bg-gray-600'}`}></span>
                                                    {customer.isAutoInvoiceEnabled !== false ? (t?.auto_invoice_on || 'Auto Factuur AAN') : (t?.auto_invoice_off || 'Auto Factuur UIT')}
                                                    {updatingCustomerId === customer.id && updatingField === 'auto_invoice' && (
                                                        <svg className="animate-spin h-2.5 w-2.5 text-current ml-0.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                    )}
                                                </button>
                                            {totalPurchasedLicenses > 0 ? (
                                                <div className="flex items-center text-[10px] font-extrabold text-primary bg-primary/5 border border-primary/10 rounded-full px-2 py-0.5 shrink-0">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1 text-primary shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                    </svg>
                                                    {totalPurchasedLicenses} {totalPurchasedLicenses === 1 ? (t?.license || 'License') : (t?.licenses || 'Licenses')}
                                                </div>
                                            ) : (
                                                <div className="flex items-center text-[10px] font-bold text-gray-400 bg-gray-50 border border-gray-100 rounded-full px-2 py-0.5 shrink-0">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                    </svg>
                                                    {t?.no_licenses || 'No Licenses'}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Comparative Debtor ID Display block */}
                                    <div className={`rounded-xl p-3 border flex flex-col space-y-2.5 ${isMismatch
                                        ? 'bg-rose-50/50 border-rose-100 text-rose-950'
                                        : 'bg-gray-50 border-gray-100 text-gray-800'
                                        }`}>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <span className={`text-[10px] font-bold uppercase tracking-wider block ${isMismatch ? 'text-rose-500' : 'text-gray-400'}`}>
                                                    {t?.afas_debtor_number || 'AFAS Debtor Number'}
                                                </span>
                                                <span className={`text-base font-extrabold ${hasDebtorId ? (isMismatch ? 'text-rose-700' : 'text-gray-800') : 'text-rose-500 italic'}`}>
                                                    {debtorStr || t?.not_configured || 'Not Configured'}
                                                </span>
                                            </div>
                                            <div className="text-right">
                                                <span className={`text-[10px] font-bold uppercase tracking-wider block ${isMismatch ? 'text-rose-500' : 'text-gray-400'}`}>
                                                    {t?.customer_id || 'Customer ID'}
                                                </span>
                                                <span className="text-xs font-mono font-bold text-gray-600 block bg-white px-2 py-0.5 rounded border border-gray-200">
                                                    {idStr || 'N/A'}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Row for AFAS Billing Debtor Number */}
                                        <div className={`flex items-center justify-between border-t border-dashed pt-2.5 ${isMismatch ? 'border-rose-200/60' : 'border-gray-200'
                                            }`}>
                                            <div>
                                                <span className={`text-[10px] font-bold uppercase tracking-wider block ${isMismatch ? 'text-rose-500' : 'text-gray-400'}`}>
                                                    {t?.afas_billing_debtor || 'AFAS Billing Debtor'}
                                                </span>
                                                <span className={`text-sm font-semibold ${billingDebtorStr ? (isMismatch ? 'text-rose-700' : 'text-gray-700') : 'text-gray-400 italic'}`}>
                                                    {billingDebtorStr || t?.no_billing_record || 'No Billing Record'}
                                                </span>
                                            </div>
                                            {isMismatch && (
                                                <span className="bg-rose-600 text-white font-bold text-[9px] uppercase px-2 py-0.5 rounded-md animate-pulse shadow-sm">
                                                    {t?.mismatch || 'Mismatch'}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Verification CTA Link */}
                                    <div className="pt-2 border-t border-gray-100 flex justify-between items-center text-xs text-primary font-bold hover:text-indigo-700">
                                        <span> </span>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm p-12 text-center border border-gray-200">
                    <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="mt-4 text-gray-500 font-medium">{t?.no_customers_found || 'No customers found matching the search criteria.'}</p>
                </div>
            )}

            <ConfirmModal
                isOpen={syncConfirmOpen}
                title={t?.sync_now_title || 'Start customer sync?'}
                message={t?.sync_now_message || 'This runs the full customer sync in the background (Google Workspace + JIRA + AFAS), refreshing debtor names, addresses, VAT numbers and contact e-mails. It can take a few minutes; customer data updates once it finishes. Track progress in "Sync Customers Logs".'}
                confirmLabel={t?.sync_now_confirm || 'Start sync'}
                cancelLabel={t?.cancel || 'Cancel'}
                onConfirm={handleManualSync}
                onCancel={() => { if (!isSyncing) setSyncConfirmOpen(false); }}
                busy={isSyncing}
                tone="primary"
            />
        </div>
    );
};

export default AdminCustomers;
